import type { PlanId } from './config';

/**
 * Plan entitlements (ADR-011), kept separate from price (config.ts) and from release status.
 *
 * Every feature records four independent facts:
 *   - `plans`      which plans include it (the intended offering);
 *   - `status`     whether it works today, assessed against the codebase, not mock screens;
 *   - `limit`      its usage limit, or null when no limit has been approved;
 *   - `condition`  any material availability condition.
 *
 * Billing period never changes entitlements. This list is not an access control: before any
 * paid functionality is enabled, the Worker must enforce entitlements server-side from
 * subscription records (ADR-011).
 */

/**
 * - `demo`: a working demonstration exists in this preview, using fictitious data only.
 * - `planned`: intended for launch (MVP-SCOPE Layers 1–2); not built yet.
 * - `later`: a later phase (MVP-SCOPE Layer 3); not part of the launch offering.
 * There is deliberately no `live` value yet: nothing in the paid offering is operational.
 */
export type FeatureStatus = 'demo' | 'planned' | 'later';

export type FeatureGroup = 'common' | 'learning' | 'professional' | 'firm';

export type Feature = {
  id: string;
  label: string;
  group: FeatureGroup;
  plans: readonly PlanId[];
  status: FeatureStatus;
  /** Approved usage limit; null until a commercial decision sets one. */
  limit: string | null;
  condition?: string;
};

const ALL: readonly PlanId[] = ['student', 'professional', 'firm'];
const PRO: readonly PlanId[] = ['professional', 'firm'];

const CONTENT_PERMISSIONS = 'Subject to the content permissions for each source.';
const UPLOAD_GATE =
  'Available only after file storage, security and AI-provider checks are complete.';

/** Rows are in comparison order: the same capability always sits in the same place. */
export const FEATURES: readonly Feature[] = [
  // Common to every paid plan.
  {
    id: 'library',
    label: 'Sierra Leone legal library with original-source viewing',
    group: 'common',
    plans: ALL,
    status: 'demo',
    limit: null,
    condition: CONTENT_PERMISSIONS,
  },
  {
    id: 'sourced-answers',
    label: 'Source-linked answers with clear research limitations',
    group: 'common',
    plans: ALL,
    status: 'demo',
    limit: null,
  },
  {
    id: 'case-briefs',
    label: 'Case briefs and judgment explanations',
    group: 'common',
    plans: ALL,
    status: 'demo',
    limit: null,
    condition: CONTENT_PERMISSIONS,
  },
  {
    id: 'saved-work',
    label: 'Personal notes, bookmarks and saved research',
    group: 'common',
    plans: ALL,
    status: 'planned',
    limit: null,
  },
  {
    id: 'export-own',
    label: 'Export of your own supported work',
    group: 'common',
    plans: ALL,
    status: 'planned',
    limit: null,
    condition: CONTENT_PERMISSIONS,
  },

  // Student: learning.
  {
    id: 'tutoring',
    label: 'AI tutoring for LLB and Law School study',
    group: 'learning',
    plans: ['student'],
    status: 'demo',
    limit: null,
  },
  {
    id: 'subjects',
    label: 'Structured subjects and guided revision',
    group: 'learning',
    plans: ['student'],
    status: 'demo',
    limit: null,
  },
  {
    id: 'quizzes',
    label: 'Practice questions and quizzes',
    group: 'learning',
    plans: ['student'],
    status: 'demo',
    limit: null,
  },
  {
    id: 'progress',
    label: 'Learning progress tracking',
    group: 'learning',
    plans: ['student'],
    status: 'planned',
    limit: null,
  },
  {
    id: 'study-documents',
    label: 'Analysis of your permitted lecture notes, judgments and study documents',
    group: 'learning',
    plans: ['student'],
    status: 'planned',
    limit: null,
    condition: UPLOAD_GATE,
  },
  {
    id: 'writing-exercises',
    label: 'Legal writing and drafting exercises',
    group: 'learning',
    plans: ['student'],
    status: 'later',
    limit: null,
  },

  // Lawyer: individual professional practice (also each Law Firm member).
  {
    id: 'pro-research',
    label: 'Professional legal research and analysis',
    group: 'professional',
    plans: PRO,
    status: 'demo',
    limit: null,
  },
  {
    id: 'drafting-tools',
    label: 'Legal document templates and drafting tools',
    group: 'professional',
    plans: PRO,
    status: 'demo',
    limit: null,
  },
  {
    id: 'memos',
    label: 'Research memoranda and legal opinion drafting',
    group: 'professional',
    plans: PRO,
    status: 'planned',
    limit: null,
  },
  {
    id: 'document-review',
    label: 'Document review and comparison',
    group: 'professional',
    plans: PRO,
    status: 'planned',
    limit: null,
    condition: UPLOAD_GATE,
  },
  {
    id: 'projects',
    label: 'Personal professional projects and saved work',
    group: 'professional',
    plans: PRO,
    status: 'planned',
    limit: null,
  },
  {
    id: 'export-pro',
    label: 'Export of research and drafts',
    group: 'professional',
    plans: PRO,
    status: 'planned',
    limit: null,
  },

  // Law Firm: organisation and collaboration (not "better answers").
  {
    id: 'team-access',
    label: 'Team invitations and role-based access',
    group: 'firm',
    plans: ['firm'],
    status: 'planned',
    limit: null,
    condition: 'Firm administrators cannot open members’ personal workspaces.',
  },
  {
    id: 'centralised-billing',
    label: 'Centralised billing for all members',
    group: 'firm',
    plans: ['firm'],
    status: 'planned',
    limit: null,
  },
  {
    id: 'firm-projects',
    label: 'Shared firm projects and authorised matter workspaces',
    group: 'firm',
    plans: ['firm'],
    status: 'later',
    limit: null,
  },
  {
    id: 'matter-permissions',
    label: 'Matter-specific permissions',
    group: 'firm',
    plans: ['firm'],
    status: 'later',
    limit: null,
  },
  {
    id: 'shared-library',
    label: 'Shared approved templates and research collections',
    group: 'firm',
    plans: ['firm'],
    status: 'later',
    limit: null,
  },
  {
    id: 'review-workflows',
    label: 'Document review and approval workflows',
    group: 'firm',
    plans: ['firm'],
    status: 'later',
    limit: null,
  },
  {
    id: 'pooled-credits',
    label: 'Pooled AI credits with administrator allocation',
    group: 'firm',
    plans: ['firm'],
    status: 'later',
    limit: null,
  },
];

export const PLAN_PURPOSE: Readonly<Record<PlanId, string>> = {
  student: 'Understand the law, prepare for examinations and develop your legal skills.',
  professional:
    'Research authorities, review documents and prepare legal drafts in your own workspace.',
  firm: 'Everything in Lawyer, with shared workspaces, team permissions and coordinated reviews.',
};

/**
 * Usage allowances. `null` means no commercial decision has been made: nothing is displayed
 * and no figure may be invented (ADR-011). `scope` is structural and already decided.
 */
export type Allowances = {
  aiCreditsPerMonth: number | null;
  storageGb: number | null;
  uploadLimit: string | null;
  scope: 'personal' | 'per-member';
};

export const ALLOWANCES: Readonly<Record<PlanId, Allowances>> = {
  student: { aiCreditsPerMonth: null, storageGb: null, uploadLimit: null, scope: 'personal' },
  professional: { aiCreditsPerMonth: null, storageGb: null, uploadLimit: null, scope: 'personal' },
  firm: { aiCreditsPerMonth: null, storageGb: null, uploadLimit: null, scope: 'per-member' },
};

export const featuresFor = (planId: PlanId) => FEATURES.filter((f) => f.plans.includes(planId));
